import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Calendar, ArrowUpRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "Website Development",
  "Business Systems",
  "Marketing & Growth",
  "Full Engagement",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.message.trim()
    ) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", preferred_date: "", preferred_time: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-16 md:py-24 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-8 lg:gap-12">
        {/* Left info */}
        <div className="col-span-12 lg:col-span-5 reveal">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#00F0FF]">
            / Contact
          </div>
          <h2 className="mt-4 font-display text-5xl md:text-6xl font-extrabold leading-[0.95] tracking-[-0.03em]">
            Tell us about
            <br />
            your project.
          </h2>
          <p className="mt-7 text-slate-400 text-lg leading-relaxed max-w-md">
            Send us a few details and we'll come back within 24 hours with the right
            people, a clear scope, and a timeline.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: Mail, label: "Email", value: "info@brandwithabdo.co.uk" },
              { icon: Calendar, label: "Booking", value: "30-min free consultation" },
              { icon: MapPin, label: "Based in", value: "London, UK · Working globally" },
            ].map((row) => (
              <div
                key={row.label}
                className="glass rounded-2xl p-5 flex items-center gap-4"
              >
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#007AFF]/15 border border-[#007AFF]/30">
                  <row.icon className="h-4.5 w-4.5 text-[#00F0FF]" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                    {row.label}
                  </div>
                  <div className="text-white font-medium">{row.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="col-span-12 lg:col-span-7 reveal glass-strong rounded-3xl p-8 md:p-10"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                Name *
              </label>
              <input
                data-testid="contact-name-input"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Jane Doe"
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                Email *
              </label>
              <input
                data-testid="contact-email-input"
                value={form.email}
                onChange={onChange("email")}
                type="email"
                placeholder="jane@company.com"
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                Phone *
              </label>
              <input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={onChange("phone")}
                type="tel"
                placeholder="+44 7700 900123"
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                Company
              </label>
              <input
                data-testid="contact-company-input"
                value={form.company}
                onChange={onChange("company")}
                placeholder="Company Ltd."
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                Service
              </label>
              <select
                data-testid="contact-service-select"
                value={form.service}
                onChange={onChange("service")}
                className={inputCls}
              >
                <option value="" className="bg-[#0a0f1c]">Choose a service</option>
                {services.map((s) => (
                  <option key={s} value={s} className="bg-[#0a0f1c]">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                  Preferred call date
                </label>
                <input
                  data-testid="contact-date-input"
                  value={form.preferred_date}
                  onChange={onChange("preferred_date")}
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                  Preferred time
                </label>
                <input
                  data-testid="contact-time-input"
                  value={form.preferred_time}
                  onChange={onChange("preferred_time")}
                  type="time"
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block">
                Project details *
              </label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={onChange("message")}
                rows={5}
                placeholder="Tell us about your goals, timeline, and any specifics."
                className={`${inputCls} resize-none`}
                required
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <p className="text-xs text-slate-500 max-w-xs">
              By submitting you agree to be contacted about your enquiry. We never share your data.
            </p>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="group inline-flex items-center gap-2 pl-7 pr-2 py-2 rounded-full bg-[#007AFF] text-white font-semibold hover:bg-[#00F0FF] hover:text-black disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
            >
              {submitting ? "Sending..." : "Send enquiry"}
              <span className="grid place-items-center h-10 w-10 rounded-full bg-black text-white group-hover:bg-white group-hover:text-black transition-colors">
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUpRight className="h-4 w-4" />
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
